import { cn, ModeToggle, toast } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { Delete } from "lucide-react";
import { useEffect, useState } from "react";
import { authClient } from "../api/client";
import { useCompetitionAuth } from "../model/useCompetitionAuth";
import { PIN_KEYPAD, PIN_MAX_LENGTH } from "../lib/pin-login-shared";

const ADMIN = {
  label: "ຜູ້ຄຸ້ມຄອງ",
  email: "competition-admin@system.local",
  activeClass: "bg-primary text-primary-foreground",
  dotClass: "bg-primary",
} as const;

/**
 * ຜູ້ຄຸ້ມຄອງເທົ່າ — ກຳມະການແຕ່ລະຄົນໃຊ້ລິ້ງສະເພາະຈາກຜູ້ຈັດ (ເບິ່ງແທັບກຳມະການໃນ admin ງານ)
 */
export function PinLoginPage() {
  const [pin, setPin] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const {
    isLoading: authLoading,
    isCompetitionUser,
    isJudge,
    getRedirectPath,
    signOut,
  } = useCompetitionAuth();

  useEffect(() => {
    if (authLoading || !isCompetitionUser) return;
    const path = getRedirectPath();
    if (path === "/competition/login" && isJudge) {
      void signOut();
      return;
    }
    navigate({ to: path });
  }, [
    authLoading,
    isCompetitionUser,
    getRedirectPath,
    navigate,
    isJudge,
    signOut,
  ]);

  const handleKey = (key: string) => {
    if (key === "⌫") {
      setPin((p) => p.slice(0, -1));
    } else if (pin.length < PIN_MAX_LENGTH) {
      setPin((p) => p + key);
    }
  };

  const handleSubmit = async () => {
    if (pin.length === 0) return;
    setIsLoading(true);
    try {
      await authClient.signIn.email(
        { email: ADMIN.email, password: pin },
        {
          onSuccess: () => {
            toast.success(`ເຂົ້າລະບົບສໍາເລັດ — ${ADMIN.label}`);
          },
          onError: (err) => {
            toast.error("PIN ບໍ່ຖືກຕ້ອງ, ກາລຸນາລອງໃໝ່");
            setPin("");
            console.error(err);
          },
        },
      );
    } catch (e) {
      toast.error("PIN ບໍ່ຖືກຕ້ອງ, ກາລຸນາລອງໃໝ່");
      setPin("");
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: auto-submit on full PIN
  useEffect(() => {
    if (pin.length === PIN_MAX_LENGTH) {
      void handleSubmit();
    }
  }, [pin]);

  if (authLoading) {
    return (
      <div className="flex h-svh items-center justify-center bg-background">
        <div className="size-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <>
      <div className="fixed top-4 right-4 z-50">
        <ModeToggle />
      </div>
      <div className="kpop-bg flex min-h-svh flex-col items-center justify-center bg-background px-4 py-8">
        <div className="mb-8 text-center">
          <p className="font-bold text-accent text-xs uppercase tracking-widest">
            KiDS EXPO 2026
          </p>
          <h1 className="kpop-gradient-text mt-1 font-extrabold text-5xl leading-tight">
            K-POP
          </h1>
          <h2 className="font-extrabold text-2xl text-foreground leading-tight">
            COVER DANCE
          </h2>
          <p className="mt-1 font-semibold text-chart-5 text-sm uppercase tracking-widest">
            Open Division
          </p>
        </div>

        <div className="w-full max-w-sm rounded-2xl bg-card p-6 shadow-2xl ring-1 ring-border">
          <p className="mb-1 text-center font-semibold text-foreground text-sm">
            ເຂົ້າລະບົບຜູ້ຄຸ້ມຄອງ
          </p>
          <p className="mb-3 text-center text-muted-foreground text-xs leading-relaxed">
            PIN ລະບົບ (ADMIN_PIN) — ກຳມະການບໍ່ໃຊ້ໜ້ານີ້; ໃຊ້ລິ້ງສະເພາະທີ່ໄດ້ຮັບຈາກຜູ້ຈັດງານ.
          </p>
          <p className="mb-3 text-center text-muted-foreground text-sm">ໃສ່ PIN</p>

          <div className="mb-6 flex justify-center gap-3">
            {Array.from({ length: PIN_MAX_LENGTH }).map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed length
                key={i}
                className={cn(
                  "size-4 rounded-full transition-all duration-150",
                  i < pin.length
                    ? `${ADMIN.dotClass} scale-110 shadow-md`
                    : "bg-muted",
                )}
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {PIN_KEYPAD.map(({ id, value }) => {
              if (value === "") return <div key={id} aria-hidden />;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleKey(value)}
                  disabled={isLoading}
                  className={cn(
                    "flex h-14 items-center justify-center rounded-xl font-bold text-xl transition-all active:scale-95 disabled:opacity-50",
                    value === "⌫"
                      ? "bg-muted text-muted-foreground hover:bg-muted/80"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                  )}
                >
                  {value === "⌫" ? <Delete className="size-5" /> : value}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isLoading || pin.length === 0}
            className={cn(
              "mt-4 w-full rounded-xl py-3 font-bold text-base transition-all active:scale-[0.98] disabled:opacity-40",
              ADMIN.activeClass,
              "shadow-lg",
            )}
          >
            {isLoading
              ? "ກໍາລັງເຂົ້າລະບົບ..."
              : `ເຂົ້າລະບົບ — ${ADMIN.label}`}
          </button>
        </div>

        <p className="mt-6 text-center text-muted-foreground/50 text-xs">
          ລະບົບໃຫ້ຄະແນນ · KiDS EXPO 2026
        </p>
      </div>
    </>
  );
}
